import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { PortalMaterialResponse } from "../../features/portal/contracts/PortalMaterialResponse";
import { fetchPortalMaterialViewUrl } from "../../features/portal/slice-extensions/portal-materials-slice-extension";
import { portalMaterialKind } from "../../features/portal/utils/portalMaterialGroups";

interface PortalMaterialViewerProps {
  memberId: string;
  material: PortalMaterialResponse;
  onClose: () => void;
}

type ViewerState = { status: "loading" } | { status: "ready"; url: string } | { status: "failed" };

/**
 * A PDF, image, audio or video file opened in the page: a full-width panel under the row on a phone, a
 * centred dialog from `md`. Each opening fetches a fresh inline URL (valid 60 minutes).
 */
const PortalMaterialViewer = ({ memberId, material, onClose }: PortalMaterialViewerProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [state, setState] = useState<ViewerState>({ status: "loading" });
  const [unsupported, setUnsupported] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const kind = portalMaterialKind(material.contentType);

  useEffect(() => {
    let live = true;
    void dispatch(fetchPortalMaterialViewUrl({ memberId, materialId: material.id })).then((result) => {
      if (!live) return;
      setState(
        fetchPortalMaterialViewUrl.fulfilled.match(result)
          ? { status: "ready", url: result.payload.url }
          : { status: "failed" },
      );
    });
    return () => {
      live = false;
    };
  }, [dispatch, memberId, material.id]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const renderFile = (url: string) => {
    if (unsupported) {
      return <p className="text-sm text-grey-40 dark:text-grey-80">{t("portal.library.viewerUnsupported")}</p>;
    }
    if (kind === "image") {
      return (
        <img
          src={url}
          alt={material.fileName}
          className="mx-auto block max-h-[70vh] w-auto max-w-full"
          onError={() => setUnsupported(true)}
          data-testid="portal-viewer-image"
        />
      );
    }
    if (kind === "audio") {
      return (
        <audio controls preload="metadata" src={url} className="w-full" onError={() => setUnsupported(true)} data-testid="portal-viewer-audio">
          {t("portal.library.viewerUnsupported")}
        </audio>
      );
    }
    if (kind === "video") {
      return (
        <video
          controls
          preload="metadata"
          playsInline
          src={url}
          className="mx-auto block max-h-[70vh] w-full rounded-lg"
          onError={() => setUnsupported(true)}
          data-testid="portal-viewer-video"
        >
          {t("portal.library.viewerUnsupported")}
        </video>
      );
    }
    return (
      <>
        <iframe src={url} title={material.fileName} className="block h-[70vh] w-full rounded-lg border border-grey-90 dark:border-primary-20" data-testid="portal-viewer-pdf" />
        <a href={url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm text-primary-40 hover:underline dark:text-primary-90">
          {t("portal.library.openInNewTab")}
        </a>
      </>
    );
  };

  return (
    <div
      className="mt-2 md:fixed md:inset-0 md:z-50 md:mt-0 md:flex md:items-center md:justify-center md:bg-black/40 md:p-4 md:dark:bg-black/70"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="false"
        aria-label={material.fileName}
        className="w-full rounded-2xl border border-grey-90 bg-white p-3 md:max-w-3xl md:p-4 md:shadow-xl dark:border-primary-20 dark:bg-primary-10"
        data-testid="portal-material-viewer"
      >
        <div className="mb-3 flex items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-grey-20 dark:text-white">{material.fileName}</p>
          <button ref={closeRef} type="button" className="btn btn-secondary" onClick={onClose} aria-label={t("portal.library.close")}>
            <X size={14} />
          </button>
        </div>
        {state.status === "loading" && <div className="h-40 rounded-lg bg-grey-95 dark:bg-primary-20" aria-busy="true" />}
        {state.status === "failed" && <p className="field-error">{t("portal.library.openFailed")}</p>}
        {state.status === "ready" && renderFile(state.url)}
      </div>
    </div>
  );
};

export default PortalMaterialViewer;
